/**
 * Email Events List Component
 *
 * Displays a table of configured email events with actions.
 */

import { Table, ActionIcon, Group, Text, Badge, Loader, Center, Stack, Button, Menu } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconDotsVertical, IconPlus } from "@tabler/icons-react";
import type { EmailEventType } from "../../../../../../../types/email-event.d";
import { getEventDisplayName, CATEGORY_NAMES, getEventCategory } from "../../email-event-metadata";
import type { EmailEventWithId } from "../../schema/email-event.types";
import { EmailEventStatusBadge } from "../email-event-status-badge";

/**
 * Props for EmailEventsList
 */
export interface EmailEventsListProperties {
  /** Array of email events to display */
  events: EmailEventWithId[];
  /** Loading state */
  loading: boolean;
  /** Callback when edit button is clicked */
  onEdit: (event: EmailEventWithId) => void;
  /** Callback when delete button is clicked */
  onDelete: (eventType: EmailEventType) => void;
  /** Callback when toggle status is clicked */
  onToggle: (eventType: EmailEventType, enabled: boolean) => void;
  /** Callback when create new event button is clicked */
  onCreate: () => void;
}

/**
 * EmailEventsList Component
 * @param root0
 * @param root0.events
 * @param root0.loading
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.onCreate
 * @example
 * <EmailEventsList
 *   events={events}
 *   loading={false}
 *   onEdit={(event) => openEditModal(event)}
 *   onDelete={(type) => deleteEvent(type)}
 *   onToggle={(type, enabled) => toggleEvent(type, enabled)}
 *   onCreate={() => openCreateModal()}
 * />
 */
export function EmailEventsList({ events, loading, onEdit, onDelete, onCreate }: EmailEventsListProperties) {
  const handleDelete = (eventType: EmailEventType, displayName: string) => {
    modals.openConfirmModal({
      title: "Confirmar exclusão",
      children: (
        <Text size="sm">
          Tem certeza que deseja remover o evento <strong>{displayName}</strong>? Esta ação não pode ser desfeita.
        </Text>
      ),
      labels: { confirm: "Remover", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => onDelete(eventType)
    });
  };

  if (loading) {
    return (
      <Center h={200}>
        <Loader />
      </Center>
    );
  }

  if (events.length === 0) {
    return (
      <Stack align="center" gap="md" py="xl">
        <Text c="dimmed" size="sm">
          Nenhum evento de email configurado
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={onCreate}>
          Criar Primeiro Evento
        </Button>
      </Stack>
    );
  }

  // Sort events by category and then by display name
  const sortedEvents = [...events].sort((a, b) => {
    const categoryA = getEventCategory(a.event_type);
    const categoryB = getEventCategory(b.event_type);

    if (categoryA !== categoryB) {
      return categoryA.localeCompare(categoryB);
    }

    return getEventDisplayName(a.event_type).localeCompare(getEventDisplayName(b.event_type));
  });

  return (
    <Table.ScrollContainer minWidth={800}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Evento</Table.Th>
            <Table.Th>Categoria</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Assunto</Table.Th>
            <Table.Th style={{ width: 120, textAlign: "right" }}>Ações</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedEvents.map((event) => {
            const displayName = getEventDisplayName(event.event_type);
            const category = getEventCategory(event.event_type);
            const categoryName = CATEGORY_NAMES[category] || category;
            const handleEditClick = () => onEdit(event);
            const handleDeleteClick = () => handleDelete(event.event_type, displayName);

            return (
              <Table.Tr key={event.id}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {displayName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {event.event_type}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" size="sm">
                    {categoryName}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <EmailEventStatusBadge enabled={event.enabled} />
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>
                    {event.subject_template || (
                      <Text c="dimmed" fs="italic">
                        Sem assunto
                      </Text>
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Menu shadow="md" width={200} position="left" withArrow arrowPosition="center">
                      <Menu.Target>
                        <ActionIcon variant="subtle" aria-label="Ações" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={handleEditClick}>Editar</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={handleDeleteClick}>
                          Remover
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

EmailEventsList.displayName = "EmailEventsList";
